import clsx from "clsx";

/**
 * Loading is paper-toned, never a spinner: blank sheets and ruled lines waiting
 * to be filled in, which is what the finished thing looks like anyway.
 */

export type SkeletonProps = {
  className?: string;
};

export function Skeleton({ className }: SkeletonProps) {
  return <span aria-hidden="true" className={clsx("skeleton block rounded-[1px]", className)} />;
}

export function SkeletonLines({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <span aria-hidden="true" className={clsx("block space-y-2", className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={clsx("h-3", index === lines - 1 ? "w-2/5" : index % 2 === 0 ? "w-full" : "w-11/12")}
        />
      ))}
    </span>
  );
}

/** A blank mounted sheet, the shape an entry card will take. */
export function EntryCardSkeleton() {
  return (
    <article className="sheet grain relative overflow-hidden p-3" aria-hidden="true">
      <Skeleton className="aspect-4/3 w-full" />
      <div className="mt-4 space-y-2 px-1 pb-1">
        <Skeleton className="h-5 w-3/5" />
        <Skeleton className="h-3 w-2/5" />
        <div className="hairline my-3" />
        <SkeletonLines lines={2} />
      </div>
    </article>
  );
}

export function CollectionCardSkeleton() {
  return (
    <article className="sheet grain overflow-hidden" aria-hidden="true">
      <Skeleton className="aspect-3/2 w-full" />
      <div className="space-y-2 p-4">
        <Skeleton className="h-5 w-4/5" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="mt-3 h-2 w-full" />
      </div>
    </article>
  );
}

export function QuarryRowSkeleton() {
  return (
    <li className="flex items-start gap-3 border-b border-rule py-3.5" aria-hidden="true">
      <Skeleton className="mt-0.5 size-5 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="h-3 w-4/5" />
      </div>
    </li>
  );
}

/**
 * A loading announcement for screen readers, so a visually calm skeleton isn't
 * a silent one.
 */
export function LoadingAnnouncement({ children }: { children: string }) {
  return (
    <p role="status" aria-live="polite" className="sr-only">
      {children}
    </p>
  );
}
